'use client';

import { token } from '@terra/ui-styled-system';
import { Badge, type BadgeProps } from '../badge';

export type BadgeHttpMethodType =
  | 'GET'
  | 'HEAD'
  | 'OPTIONS'
  | 'POST'
  | 'PATCH'
  | 'PUT'
  | 'DELETE';

export type BadgeHttpProps = BadgeProps & {
  method?: string;
  status?: number;
};

type BadgeMethodConfig = {
  bg: string;
  borderColor: string;
};

const mapStatusCodeToMethod = ({
  status,
}: {
  status: number;
}): BadgeHttpMethodType | null => {
  if (status >= 200 && status < 300) return 'GET';
  if (status >= 300 && status < 400) return 'POST';
  if (status >= 400 && status < 500) return 'PUT';
  if (status >= 500) return 'DELETE';
  return null;
};

const BADGE_HTTP_CONFIG: Record<BadgeHttpMethodType, BadgeMethodConfig> = {
  GET: {
    bg: token('colors.bg.severity.low'),
    borderColor: token('colors.border.severity.low'),
  },
  HEAD: {
    bg: token('colors.bg.severity.low'),
    borderColor: token('colors.border.severity.low'),
  },
  OPTIONS: {
    bg: token('colors.bg.severity.low'),
    borderColor: token('colors.border.severity.low'),
  },
  POST: {
    bg: token('colors.bg.severity.medium'),
    borderColor: token('colors.border.severity.medium'),
  },
  PATCH: {
    bg: token('colors.bg.severity.medium'),
    borderColor: token('colors.border.severity.medium'),
  },
  PUT: {
    bg: token('colors.bg.severity.high'),
    borderColor: token('colors.border.severity.high'),
  },
  DELETE: {
    bg: token('colors.bg.severity.critical'),
    borderColor: token('colors.border.severity.critical'),
  },
};

const isBadgeHttpMethod = (method: string): method is BadgeHttpMethodType =>
  method in BADGE_HTTP_CONFIG;

const normalizeBadgeHttpMethod = ({
  method,
}: {
  method?: string;
}): BadgeHttpMethodType | null => {
  if (!method) return null;

  const normalizedMethod = method.toUpperCase();
  if (isBadgeHttpMethod(normalizedMethod)) {
    return normalizedMethod;
  }

  return null;
};

export const BadgeHttp = ({
  method,
  status,
  variant = 'emphasis',
  size = 'md',
  children,
  ...props
}: BadgeHttpProps) => {
  const normalizedMethod = normalizeBadgeHttpMethod({ method });
  const resolvedMethod =
    normalizedMethod ?? (status ? mapStatusCodeToMethod({ status }) : null);
  const config = resolvedMethod ? BADGE_HTTP_CONFIG[resolvedMethod] : null;

  return (
    <Badge
      variant={variant}
      size={size}
      bg={config?.bg}
      borderColor={config?.borderColor}
      {...props}
    >
      {children}
    </Badge>
  );
};
